import { useState, useEffect, useCallback } from 'react';
import {
  Typography, Box,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Alert, CircularProgress, Snackbar, Paper,
} from '@mui/material';
import { getGames, enterLottery } from '../api';

const CAMDEN_YARDS = '/baseball.jpg';
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Parse YYYY-MM-DD without UTC shift
const parseDate = (str) => {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const fmtDate = (str) => {
  if (!str) return '';
  return parseDate(str).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
  });
};

const fmtTime = (timeStr) => {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const fmtDeadlineDisplay = (str) => {
  if (!str) return '';
  const [y, m, d] = str.split('-').map(Number);
  const date = new Date(y, m - 1, d - 1); // show one day prior
  return date.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
  });
};

// Group games by "YYYY-MM" month key
function groupByMonth(games) {
  const map = {};
  for (const g of games) {
    const key = g.date.slice(0, 7);
    if (!map[key]) map[key] = [];
    map[key].push(g);
  }
  return map;
}

// Build a 6-row calendar grid for a given year/month (0-indexed)
function buildGrid(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

// ── Month Calendar ────────────────────────────────────────────────────────
function MonthCalendar({ year, month, games, onSelectGame }) {
  const grid = buildGrid(year, month);
  const monthLabel = new Date(year, month, 1).toLocaleDateString('en-US', {
    month: 'long', year: 'numeric'
  });

  // Map day-of-month → array of games (multiple games can fall on same day)
  const gamesByDay = {};
  for (const g of games) {
    const d = parseInt(g.date.split('-')[2], 10);
    if (!gamesByDay[d]) gamesByDay[d] = [];
    gamesByDay[d].push(g);
  }

  return (
    <Box sx={{ mb: 5 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
        {monthLabel}
      </Typography>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Day headers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', bgcolor: 'secondary.main' }}>
          {DAYS.map(d => (
            <Box key={d} sx={{ py: 0.75, textAlign: 'center' }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: '#fff' }}>
                {d}
              </Typography>
            </Box>
          ))}
        </Box>
        {/* Date cells */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', bgcolor: 'background.paper' }}>
          {grid.map((day, i) => {
            const dayGames = day ? (gamesByDay[day] || []) : [];
            const hasGames = dayGames.length > 0;
            const singleGame = dayGames.length === 1 ? dayGames[0] : null;
            return (
              <Box
                key={i}
                onClick={singleGame ? () => onSelectGame(singleGame) : undefined}
                sx={{
                  minHeight: 72,
                  p: 0.75,
                  borderTop: '1px solid',
                  borderLeft: i % 7 !== 0 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  cursor: singleGame ? 'pointer' : 'default',
                  bgcolor: hasGames ? 'rgba(223, 70, 1, 0.06)' : 'transparent',
                  '&:hover': singleGame ? { bgcolor: 'rgba(223, 70, 1, 0.12)' } : {},
                  transition: 'background-color 0.15s',
                }}
              >
                {day && (
                  <>
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: hasGames ? 'primary.main' : 'transparent',
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={hasGames ? 700 : 400}
                        sx={{ color: hasGames ? '#fff' : 'text.secondary', lineHeight: 1 }}
                      >
                        {day}
                      </Typography>
                    </Box>
                    {dayGames.map((g, gi) => {
                      const closed = g.deadline && parseDate(g.deadline) < new Date();
                      const isMulti = dayGames.length > 1;
                      return (
                        <Typography
                          key={gi}
                          onClick={isMulti ? (e) => { e.stopPropagation(); onSelectGame(g); } : undefined}
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: closed ? 'text.disabled' : 'primary.main',
                            fontWeight: 600,
                            lineHeight: 1.2,
                            fontSize: '0.65rem',
                            cursor: isMulti ? 'pointer' : 'inherit',
                            mb: 0.25,
                            '&:hover': isMulti ? { textDecoration: 'underline' } : {},
                          }}
                        >
                          vs. {g.opponent}
                          {closed && ' (Closed)'}
                        </Typography>
                      );
                    })}
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}

// ── Main EmployeeView ─────────────────────────────────────────────────────
export default function EmployeeView({ employeeName = '' }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedGame, setSelectedGame] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [snack, setSnack] = useState(null);

  const loadGames = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getGames();
      const now = new Date();
      const filtered = data.filter(g => {
        const gameDate = new Date(g.date + 'T23:59:59');
        return gameDate >= now && !g.hidden;
      });
      const sorted = [...filtered].sort((a, b) => new Date(a.date) - new Date(b.date));
      setGames(sorted);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGames(); }, [loadGames]);

  const handleSelectGame = (game) => {
    setSelectedGame(game);
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (!employeeName.trim()) {
      setSubmitError('Could not determine your name from SSO. Please sign out and try again.');
      return;
    }
    const isDeadlinePassed = selectedGame?.deadline && parseDate(selectedGame.deadline) < new Date();
    if (isDeadlinePassed) {
      setSubmitError('The entry deadline for this game has passed.');
      return;
    }
    try {
      setSubmitting(true);
      setSubmitError(null);
      await enterLottery(selectedGame.gameId, {
        employeeName: employeeName.trim(),
        ticketsRequested: selectedGame.ticketsPerWinner ?? 4,
      });
      setSnack(`You're entered in the lottery for ${selectedGame.opponent}!`);
      setSelectedGame(null);
    } catch (e) {
      setSubmitError(
        e.message === 'Already entered'
          ? 'You have already entered the lottery for this game.'
          : e.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => { setSelectedGame(null); setSubmitError(null); };

  const monthGroups = groupByMonth(games);
  const monthKeys = Object.keys(monthGroups).sort();
  const isDeadlinePassed = selectedGame?.deadline && parseDate(selectedGame.deadline) < new Date();
  const ticketsForGame = selectedGame?.ticketsPerWinner ?? 4;

  return (
    <>
      {/* Hero Banner */}
      <Paper
        elevation={3}
        sx={{
          mb: 4,
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          height: { xs: 160, sm: 220, md: 280 },
        }}
      >
        <Box
          component="img"
          src={CAMDEN_YARDS}
          alt="Camden Yards"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 60%',
            display: 'block',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to right, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0) 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: { xs: 3, sm: 5 },
          }}
        >
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{ color: '#fff', lineHeight: 1.2, mb: 0.5, textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}
          >
            ⚾ Win Tickets to<br />Camden Yards
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'rgba(255,255,255,0.85)', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}
          >
            Enter the lottery for a chance at 4 seats per game.
          </Typography>
        </Box>
      </Paper>

      <Typography variant="h5" fontWeight={700} gutterBottom>
        Upcoming Games
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>Failed to load games: {error}</Alert>}

      {!loading && !error && games.length === 0 && (
        <Typography color="text.secondary">No upcoming games available.</Typography>
      )}

      {!loading && !error && monthKeys.map(key => {
        const [y, m] = key.split('-').map(Number);
        return (
          <MonthCalendar
            key={key}
            year={y}
            month={m - 1}
            games={monthGroups[key]}
            onSelectGame={handleSelectGame}
          />
        );
      })}

      {/* Game Detail + Entry Dialog */}
      <Dialog open={!!selectedGame} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>vs. {selectedGame?.opponent}</DialogTitle>
        <DialogContent>
          {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            📅 {fmtDate(selectedGame?.date)}{selectedGame?.time ? ` · ${fmtTime(selectedGame.time)}` : ''}
          </Typography>
          {selectedGame?.deadline && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              ⏰ Entry deadline: {fmtDeadlineDisplay(selectedGame.deadline)}
            </Typography>
          )}
          {!isDeadlinePassed && (
            <Typography variant="body2" color="text.secondary">
              Entering as <strong>{employeeName}</strong> for <strong>{ticketsForGame} tickets</strong>.
            </Typography>
          )}
          {isDeadlinePassed && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              The entry deadline for this game has passed.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>Close</Button>
          {!isDeadlinePassed && (
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              {submitting ? <CircularProgress size={20} /> : 'Enter Lottery'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        message={snack}
      />
    </>
  );
}
